/**
 * useLocalePricing Hook
 * 
 * Builds locale-aware pricing arrays from DB-backed locale pricing data.
 * For US visitors, returns the hardcoded USD prices.
 * For international visitors (UK, AU, EU), fetches prices from the DB
 * and uses Intl.NumberFormat for locale-specific formatting.
 * 
 * Price key convention in DB:
 *   Base product:
 *   - standard_1_price, standard_1_cap (compare at price)
 *   - standard_2_price, standard_2_cap
 *   - standard_4_price, standard_4_cap
 *   - king_1_price, king_1_cap
 *   - king_2_price, king_2_cap
 *   - king_4_price, king_4_cap
 * 
 *   Upsells:
 *   - upsell_pillow_standard_price, upsell_pillow_standard_original
 *   - upsell_pillow_king_price, upsell_pillow_king_original
 *   - upsell_pillowcase_standard_price, upsell_pillowcase_standard_original
 *   - upsell_pillowcase_king_price, upsell_pillowcase_king_original
 * 
 *   Shipping:
 *   - shipping_1, shipping_2, shipping_4
 *   - shipping_insurance
 * 
 *   Bonus:
 *   - bonus_pillowcase_standard_value, bonus_pillowcase_king_value
 * 
 *   Marketing:
 *   - marketing_four_seasons, marketing_ritz_carlton, marketing_marriott
 *   - marketing_retail_price
 *   - marketing_cost_manufacturing, marketing_cost_distributor, marketing_cost_rent, marketing_cost_staff
 *   - marketing_fluffco_manufacturing, marketing_fluffco_shipping, marketing_fluffco_us
 * 
 * Formatting conventions:
 *   - UK (GBP): £59.00 (symbol before, dot decimal, 2 decimals)
 *   - AU (AUD): A$99.00 (symbol before, dot decimal, 2 decimals)
 *   - EU (EUR): 69,00 € (symbol after, comma decimal, 2 decimals)
 *   - US (USD): $69.90 (symbol before, dot decimal, 2 decimals)
 */

import { useMemo } from 'react';
import { useLocale } from '@/contexts/LocaleContext';

// ─── US Default Prices (hardcoded fallback) ─────────────────────────────────
/** Auto-calculate savings and discount from price/originalPrice */
function calcTier(t: { quantity: number; price: number; originalPrice: number; badge?: string; bonus?: boolean }) {
  const savings = Math.round((t.originalPrice - t.price) * 100) / 100;
  const discount = Math.round((savings / t.originalPrice) * 100);
  return { ...t, savings, discount };
}

const US_STANDARD_PRICING = [
  calcTier({ quantity: 1, price: 55.90, originalPrice: 109.90 }),
  calcTier({ quantity: 2, price: 85.90, originalPrice: 199.90, badge: "Most Popular" }),
  calcTier({ quantity: 4, price: 129.90, originalPrice: 379.90, badge: "Best Value", bonus: true }),
];

const US_KING_PRICING = [
  calcTier({ quantity: 1, price: 55.90, originalPrice: 109.90 }),
  calcTier({ quantity: 2, price: 85.90, originalPrice: 199.90, badge: "Most Popular" }),
  calcTier({ quantity: 4, price: 129.90, originalPrice: 379.90, badge: "Best Value", bonus: true }),
];

// ─── US Default Upsell Prices ─────────────────────────────────
const US_UPSELL_PILLOW = {
  standard: { price: 35.90, originalPrice: 109.90 },
  king: { price: 35.90, originalPrice: 109.90 },
};

const US_UPSELL_PILLOWCASE = {
  standard: { price: 34.90, originalPrice: 55.90 },
  king: { price: 34.90, originalPrice: 55.90 },
};

const US_UPSELL_EYEMASK = { price: 29, originalPrice: 49 };

// ─── US Default Gift Thresholds & Values ─────────────────────────────────
const US_GIFT_TIERS = {
  shipping: { threshold: 50, value: 9.99 },
  slippers: { threshold: 150, value: 28 },
  spray: { threshold: 250, value: 129 },
};

// ─── US Default Shipping ─────────────────────────────────
const US_SHIPPING = { 1: 0, 2: 0, 4: 0 }; // Free in US
const US_INTL_SHIPPING = { 1: 10, 2: 15, 4: 20 }; // Non-US default
const US_INSURANCE = 5.00;

// ─── US Default Bonus Values ─────────────────────────────────
const US_BONUS_PILLOWCASE = { standard: 156, king: 196 };

// ─── US Default Marketing Prices ─────────────────────────────────
const US_MARKETING = {
  fourSeasons: 200,
  ritzCarlton: 140,
  marriott: 130,
  retailPrice: 125.90,
  costManufacturing: 17.50,
  costDistributor: 25.00,
  costRent: 25.00,
  costStaff: 18.40,
  fluffcoManufacturing: 17.50,
  fluffcoShipping: 10.98,
  fluffcoUs: 13.00,
};

export interface PricingOption {
  quantity: number;
  price: number;
  originalPrice: number;
  savings: number;
  discount: number;
  badge?: string;
  bonus?: boolean;
}

export interface UpsellPricing {
  price: number;
  originalPrice: number;
  savings: number;
  savingsPercent: number;
}

export interface GiftTierPricing {
  shipping: { threshold: number; value: number };
  slippers: { threshold: number; value: number };
  spray: { threshold: number; value: number };
}

export interface MarketingPrices {
  fourSeasons: number;
  ritzCarlton: number;
  marriott: number;
  retailPrice: number;
  costManufacturing: number;
  costDistributor: number;
  costRent: number;
  costStaff: number;
  fluffcoManufacturing: number;
  fluffcoShipping: number;
  fluffcoUs: number;
}

/**
 * Build pricing array from locale pricing map
 * Falls back to US prices if any required key is missing
 */
function buildPricingFromLocale(
  pricing: Record<string, number>,
  size: 'standard' | 'king',
  usFallback: PricingOption[]
): PricingOption[] {
  const quantities = [1, 2, 4] as const;
  const result: PricingOption[] = [];

  for (let i = 0; i < quantities.length; i++) {
    const qty = quantities[i];
    const priceKey = `${size}_${qty}_price`;
    const capKey = `${size}_${qty}_cap`;
    
    const price = pricing[priceKey];
    const cap = pricing[capKey];
    
    if (price !== undefined && cap !== undefined) {
      const savings = cap - price;
      const discount = Math.round((savings / cap) * 100);
      result.push({
        quantity: qty,
        price,
        originalPrice: cap,
        savings,
        discount,
        ...(usFallback[i]?.badge ? { badge: usFallback[i].badge } : {}),
        ...(usFallback[i]?.bonus ? { bonus: true } : {}),
      });
    } else {
      // Missing locale price → fall back to US for this tier
      result.push(usFallback[i]);
    }
  }

  return result;
}

/**
 * Build upsell pricing from locale pricing map
 */
function buildUpsellPricing(
  pricing: Record<string, number>,
  priceKey: string,
  originalKey: string,
  usFallback: { price: number; originalPrice: number }
): UpsellPricing {
  const price = pricing[priceKey] ?? usFallback.price;
  const originalPrice = pricing[originalKey] ?? usFallback.originalPrice;
  const savings = originalPrice - price;
  const savingsPercent = Math.round((savings / originalPrice) * 100);
  return { price, originalPrice, savings, savingsPercent };
}

export function useLocalePricing() {
  const { locale, pricing, pricingLoaded, formatPrice, formatPriceCompact, isInternational, getPrice } = useLocale();

  // Build locale-aware pricing arrays
  const standardPricing = useMemo<PricingOption[]>(() => {
    if (!isInternational || !pricingLoaded) return US_STANDARD_PRICING;
    return buildPricingFromLocale(pricing, 'standard', US_STANDARD_PRICING);
  }, [isInternational, pricingLoaded, pricing]);

  const kingPricing = useMemo<PricingOption[]>(() => {
    if (!isInternational || !pricingLoaded) return US_KING_PRICING;
    return buildPricingFromLocale(pricing, 'king', US_KING_PRICING);
  }, [isInternational, pricingLoaded, pricing]);

  // ─── Upsell Pricing (locale-aware) ─────────────────────────────────
  const getUpsellPillow = useMemo(() => {
    return (size: "Standard" | "King"): UpsellPricing => {
      const sizeKey = size.toLowerCase() as 'standard' | 'king';
      if (!isInternational || !pricingLoaded) {
        const us = US_UPSELL_PILLOW[sizeKey];
        const savings = us.originalPrice - us.price;
        return { ...us, savings, savingsPercent: Math.round((savings / us.originalPrice) * 100) };
      }
      return buildUpsellPricing(
        pricing,
        `upsell_pillow_${sizeKey}_price`,
        `upsell_pillow_${sizeKey}_original`,
        US_UPSELL_PILLOW[sizeKey]
      );
    };
  }, [isInternational, pricingLoaded, pricing]);

  const getUpsellPillowcase = useMemo(() => {
    return (size: "Standard" | "King"): UpsellPricing => {
      const sizeKey = size.toLowerCase() as 'standard' | 'king';
      if (!isInternational || !pricingLoaded) {
        const us = US_UPSELL_PILLOWCASE[sizeKey];
        const savings = us.originalPrice - us.price;
        return { ...us, savings, savingsPercent: Math.round((savings / us.originalPrice) * 100) };
      }
      return buildUpsellPricing(
        pricing,
        `upsell_pillowcase_${sizeKey}_price`,
        `upsell_pillowcase_${sizeKey}_original`,
        US_UPSELL_PILLOWCASE[sizeKey]
      );
    };
  }, [isInternational, pricingLoaded, pricing]);

  // ─── Upsell Eyemask Pricing (locale-aware) ─────────────────────────────────
  const getUpsellEyemask = useMemo<UpsellPricing>(() => {
    if (!isInternational || !pricingLoaded) {
      const us = US_UPSELL_EYEMASK;
      const savings = us.originalPrice - us.price;
      return { ...us, savings, savingsPercent: Math.round((savings / us.originalPrice) * 100) };
    }
    return buildUpsellPricing(
      pricing,
      'upsell_eyemask_price',
      'upsell_eyemask_original',
      US_UPSELL_EYEMASK
    );
  }, [isInternational, pricingLoaded, pricing]);

  // ─── Gift Tier Thresholds & Values (locale-aware) ─────────────────────────────────
  const giftTiers = useMemo<GiftTierPricing>(() => {
    if (!isInternational || !pricingLoaded) return US_GIFT_TIERS;
    return {
      shipping: {
        threshold: pricing['gift_shipping_threshold'] ?? US_GIFT_TIERS.shipping.threshold,
        value: pricing['gift_shipping_value'] ?? US_GIFT_TIERS.shipping.value,
      },
      slippers: {
        threshold: pricing['gift_slippers_threshold'] ?? US_GIFT_TIERS.slippers.threshold,
        value: pricing['gift_slippers_value'] ?? US_GIFT_TIERS.slippers.value,
      },
      spray: {
        threshold: pricing['gift_spray_threshold'] ?? US_GIFT_TIERS.spray.threshold,
        value: pricing['gift_spray_value'] ?? US_GIFT_TIERS.spray.value,
      },
    };
  }, [isInternational, pricingLoaded, pricing]);

  // ─── Shipping Costs (locale-aware) ─────────────────────────────────
  const getShippingCost = useMemo(() => {
    return (countryCode: string, quantity: number): number => {
      // US gets free shipping
      if (countryCode === 'US') return 0;

      // For international locales, use DB prices if available
      if (isInternational && pricingLoaded) {
        // Map quantity to shipping tier key
        const tierKey = quantity <= 1 ? 'shipping_1' : quantity === 2 ? 'shipping_2' : 'shipping_4';
        const dbPrice = pricing[tierKey];
        if (dbPrice !== undefined) return dbPrice;
      }

      // Fallback to hardcoded international shipping
      if (quantity <= 1) return US_INTL_SHIPPING[1];
      if (quantity === 2) return US_INTL_SHIPPING[2];
      return US_INTL_SHIPPING[4];
    };
  }, [isInternational, pricingLoaded, pricing]);

  // ─── Shipping Insurance (locale-aware) ─────────────────────────────────
  const insuranceCost = useMemo(() => {
    if (!isInternational || !pricingLoaded) return US_INSURANCE;
    return pricing['shipping_insurance'] ?? US_INSURANCE;
  }, [isInternational, pricingLoaded, pricing]);

  // ─── Bonus Pillowcase Value (locale-aware) ─────────────────────────────────
  const getBonusPillowcaseValue = useMemo(() => {
    return (size: "Standard" | "King"): number => {
      const sizeKey = size.toLowerCase() as 'standard' | 'king';
      if (!isInternational || !pricingLoaded) return US_BONUS_PILLOWCASE[sizeKey];
      return pricing[`bonus_pillowcase_${sizeKey}_value`] ?? US_BONUS_PILLOWCASE[sizeKey];
    };
  }, [isInternational, pricingLoaded, pricing]);

  // ─── Marketing Prices (locale-aware) ─────────────────────────────────
  const marketingPrices = useMemo<MarketingPrices>(() => {
    if (!isInternational || !pricingLoaded) return US_MARKETING;
    return {
      fourSeasons: pricing['marketing_four_seasons'] ?? US_MARKETING.fourSeasons,
      ritzCarlton: pricing['marketing_ritz_carlton'] ?? US_MARKETING.ritzCarlton,
      marriott: pricing['marketing_marriott'] ?? US_MARKETING.marriott,
      retailPrice: pricing['marketing_retail_price'] ?? US_MARKETING.retailPrice,
      costManufacturing: pricing['marketing_cost_manufacturing'] ?? US_MARKETING.costManufacturing,
      costDistributor: pricing['marketing_cost_distributor'] ?? US_MARKETING.costDistributor,
      costRent: pricing['marketing_cost_rent'] ?? US_MARKETING.costRent,
      costStaff: pricing['marketing_cost_staff'] ?? US_MARKETING.costStaff,
      fluffcoManufacturing: pricing['marketing_fluffco_manufacturing'] ?? US_MARKETING.fluffcoManufacturing,
      fluffcoShipping: pricing['marketing_fluffco_shipping'] ?? US_MARKETING.fluffcoShipping,
      fluffcoUs: pricing['marketing_fluffco_us'] ?? US_MARKETING.fluffcoUs,
    };
  }, [isInternational, pricingLoaded, pricing]);

  /**
   * Format a price amount using the current locale's conventions.
   * Uses Intl.NumberFormat under the hood for proper symbol placement,
   * decimal separator, and thousands separator.
   * 
   * Examples:
   *   fp(69.90)  → "$69.90" (US) | "£69.90" (UK) | "AUD $99.00" (AU) | "69,90 €" (EU)
   */
  const fp = formatPrice;

  /**
   * Format a price compactly - no decimals for whole numbers.
   * 
   * Examples:
   *   fpc(69)    → "$69" (US) | "£69" (UK) | "AUD $99" (AU) | "69 €" (EU)
   *   fpc(69.90) → "$69.90" (US) | "£69.90" (UK) | etc.
   */
  const fpc = formatPriceCompact;

  /**
   * Format a price as integer only (floor), using compact format.
   * Useful for hero price displays where decimals are not shown.
   * 
   * Examples:
   *   fpInt(69.90) → "$69" (US) | "£69" (UK) | "AUD $99" (AU) | "69 €" (EU)
   */
  const fpInt = useMemo(() => {
    return (amount: number): string => formatPriceCompact(Math.floor(amount));
  }, [formatPriceCompact]);

  const getPricing = useMemo(() => {
    return (size: "Standard" | "King"): PricingOption[] => {
      return size === "Standard" ? standardPricing : kingPricing;
    };
  }, [standardPricing, kingPricing]);

  return {
    /** Standard size pricing array (locale-aware) */
    standardPricing,
    /** King size pricing array (locale-aware) */
    kingPricing,
    /** Get pricing by size name */
    getPricing,
    /** Format price with full decimals: $69.90, £69.00, 69,00 € */
    fp,
    /** Format price compact (no decimals for whole numbers): $69, £69, 69 € */
    fpc,
    /** Format price as integer (floor): $69, £69, 69 € */
    fpInt,
    /** Current locale info */
    locale,
    /** Whether user is on an international locale */
    isInternational,
    /** Whether pricing data has been loaded */
    pricingLoaded,
    /** Currency code (USD, GBP, AUD, EUR) */
    currencyCode: locale.currencyCode,
    /** Currency symbol ($, £, A$, €) */
    currencySymbol: locale.currencySymbol,

    // ─── New locale-aware pricing getters ─────────────────────────────────
    /** Get upsell pillow pricing by size (locale-aware) */
    getUpsellPillow,
    /** Get upsell pillowcase pricing by size (locale-aware) */
    getUpsellPillowcase,
    /** Get upsell silk eyemask pricing (locale-aware) */
    getUpsellEyemask,
    /** Get shipping cost by country code and quantity (locale-aware) */
    getShippingCost,
    /** Shipping insurance cost (locale-aware) */
    insuranceCost,
    /** Get bonus pillowcase value by size (locale-aware) */
    getBonusPillowcaseValue,
    /** Marketing comparison prices (locale-aware) */
    marketingPrices,
    /** Gift tier thresholds and values (locale-aware) */
    giftTiers,
    /** Raw getPrice from locale context for ad-hoc lookups */
    getPrice,
  };
}
